import { useState } from 'react';
import { api } from '../lib/api';
import { ErrorBox } from '../components/DataTable';

type Plan = Awaited<ReturnType<typeof api.syncDyopsTeams>>;

/**
 * 「DYOPS 에서 팀 가져오기」(2026-09-28 way 「팀정보는 dyops 에서 연동」) — 미리보기 → 반영 2단계.
 * 매일 06:00 에도 자동으로 돈다(`dyopsTeamSync.ts`). DYOPS 에 팀이 비어 있는 사람은 여기 값을 그대로 둔다.
 */
export default function DyopsTeamSync({ onApplied }: { onApplied: () => void }) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function run(dryRun: boolean) {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const r = await api.syncDyopsTeams({ dryRun });
      if (dryRun) setPlan(r);
      else {
        setPlan(null);
        setDone(`${r.applied}명의 팀을 DYOPS 에 맞췄습니다.`);
        onApplied();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-2 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="break-keep text-sm text-ink-soft">
          <b>팀</b>은 DYOPS 사용자 정보에서 가져옵니다(오전 6시~밤 10시 매시 정각 자동). DYOPS 에서 팀을 바꾸면 여기에도 반영됩니다.
          DYOPS 에 팀이 비어 있는 사람은 여기 값을 그대로 둡니다.
        </p>
        <button className="btn-ghost" disabled={busy} onClick={() => void run(true)}>
          {busy && !plan ? '확인 중…' : 'DYOPS 에서 팀 가져오기'}
        </button>
      </div>
      {error && <ErrorBox message={error} />}
      {done && <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800">{done}</div>}
      {plan && (
        <div className="space-y-2 rounded-lg border border-brand-100 bg-brand-50 p-3 text-sm">
          <p>
            DYOPS 팀 있는 사람 {plan.dyopsWithTeam}명 · 이메일이 맞는 계정 {plan.matched}명 ·{' '}
            <b>바뀔 사람 {plan.changes.length}명</b>
          </p>
          {plan.changes.length > 0 && (
            <ul className="max-h-60 space-y-0.5 overflow-auto">
              {plan.changes.map((c) => (
                <li key={c.appUserId}>
                  {c.name || c.email} <span className="text-ink-faint">({c.email})</span> :{' '}
                  <span className="text-ink-faint">{c.from || '없음'}</span> → <b>{c.to}</b>
                </li>
              ))}
            </ul>
          )}
          {plan.renames.length > 0 && (
            <p className="text-ink-faint">
              팀 이름 바뀜: {plan.renames.map((r) => `${r.from} → ${r.to}`).join(', ')} — 그 팀의 화면 권한·기준정보 소속 범위도
              새 이름으로 옮깁니다.
            </p>
          )}
          <div className="flex gap-2">
            {plan.changes.length > 0 && (
              <button className="btn-primary" disabled={busy} onClick={() => void run(false)}>
                {plan.changes.length}명 반영
              </button>
            )}
            <button className="btn-ghost" disabled={busy} onClick={() => setPlan(null)}>
              닫기
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
